import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { GraduationCap, BookOpen, Heart, Users, ArrowRight, Sparkles } from 'lucide-react';
export const SchoolsHighlightSection = () => {
  const pillars = [{
    icon: Heart,
    title: 'Identity & Belonging',
    body: 'Especially meaningful for Punjabi Sikh students.'
  }, {
    icon: BookOpen,
    title: 'Sikh History, Made Vivid',
    body: 'Story-led, immersive entry into a powerful chapter of history.'
  }, {
    icon: Users,
    title: 'Cultural Understanding for All',
    body: 'Empathy, literacy, and connection across communities.'
  }];
  return <section id="schools" className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-1841" data-mp="ol19286o7u8t">
      {/* atmospheric background */}
      <div className="absolute inset-0" data-id="element-1842" data-mp="200g61byvqm3x">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-10" data-id="element-1843" data-mp="1zzeiw94la95u" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black" data-id="element-1844" data-mp="3pf0myx0xu38r" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-1845" data-mp="3ucshzzwvq5h6" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-1846" data-mp="31o4qvnpg8lvp">
        <div className="max-w-6xl mx-auto" data-id="element-1847" data-mp="31likhreaocf2">
          {/* ---------- Heading ---------- */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-14 max-w-3xl mx-auto" data-id="element-1848" data-mp="36k24vnd8pg5b">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 mb-6" data-id="element-1849" data-mp="2mw2uidml8b0f">
              <GraduationCap className="w-3.5 h-3.5 text-amber-400" data-id="element-1850" data-mp="3la9pbocopfa8" />
              <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-light" data-id="element-1851" data-mp="3la00i43dle64">
                For Schools &amp; Curriculum Partners
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-6 leading-[1.05] tracking-tight" data-id="element-1852" data-mp="3v0l6icldpkz2">
              Bring Sikh History to{' '}
              <span className="text-amber-400" data-id="element-1853" data-mp="32bxjockzenje">Life for Students</span>
            </h2>
            <div className="w-20 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-1854" data-mp="3gax7ji0o6yei" />
            <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-1855" data-mp="3g92j3tq6wo5o">
              An immersive Sikh history learning experience that supports
              identity, belonging, cultural understanding, and student
              engagement — designed for middle school and high school groups.
            </p>
          </motion.div>

          {/* ---------- Three Pillars ---------- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-id="element-1856" data-mp="z586roxsb5d7">
            {pillars.map((p, i) => <motion.div key={p.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative border border-amber-400/15 hover:border-amber-400/40 transition-colors p-6 bg-gradient-to-b from-amber-950/8 to-transparent" data-id="element-1857" data-mp="p4pimhz6t2ph" data-mpi={i}>
                <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-1858" data-mp="20r6abptrqk56" />
                <div className="w-9 h-9 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-4" data-id="element-1859" data-mp="25l0qwke2uj01">
                  <p.icon className="w-4 h-4 text-amber-400" data-id="element-1860" data-mp="cvj9ri63t2t5" />
                </div>
                <h3 className="font-serif text-lg text-white mb-2 leading-tight" data-id="element-1861" data-mp="cxmaaw4vuec4">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-1862" data-mp="2xlpf5rd7q5t">
                  {p.body}
                </p>
              </motion.div>)}
          </div>

          {/* ---------- Grade-level + CTA strip ---------- */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative border border-amber-400/25 bg-gradient-to-br from-amber-950/15 via-black/40 to-black p-6 sm:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10" data-id="element-1863" data-mp="vmj46ywdb4tt">
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-1864" data-mp="1quut9ufy6maq" />

            <div className="flex-1" data-id="element-1865" data-mp="1vt61k0qlgoqu">
              <div className="flex items-center gap-3 mb-3" data-id="element-1866" data-mp="1vwaepnm1eutn">
                <Sparkles className="w-4 h-4 text-amber-400" data-id="element-1867" data-mp="20ubv20s2wg1f" />
                <span className="text-amber-400 tracking-[0.3em] text-[10px] uppercase font-light" data-id="element-1868" data-mp="25sn78i5uj2mt">
                  Middle School · High School · Curriculum Partners
                </span>
              </div>
              <p className="font-serif text-xl sm:text-2xl text-white leading-snug max-w-2xl" data-id="element-1869" data-mp="2ek704nnsyi9v">
                Group visits, educator previews, curriculum enrichment, and
                pilot partnerships — designed to{' '}
                <span className="text-amber-400" data-id="element-1870" data-mp="2ojxyj0fhhhf8">complement and enrich</span>{' '}
                existing learning.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-1871" data-mp="23ttxsbnqne74">
              <Link to="/schools" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_30px_rgba(212,175,55,0.25)]" data-id="element-1872" data-mp="23u3o60cp3ndc">
                Explore the School Program
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-1873" data-mp="2mjseth9ikhmw" />
              </Link>
              <Link to="/schools#interest-form" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-1874" data-mp="2hkfj070o0086">
                Request a Conversation
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};